import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, LogOut, User } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const links = [
    { label: "Home", to: "/" },
    { label: "Coaching", to: "/coaching" },
    { label: "Immigration", to: "/immigration" },
    { label: "E-Books", to: "/ebooks" },
    { label: "Blog", to: "/blog" },
    { label: "Contact", to: "/#contact", scrollTo: "contact" },
  ];

  const handleNavClick = (e, link) => {
    if (!link.scrollTo) return;
    // On home page, prevent full navigation and smooth-scroll instead
    if (location.pathname === "/") {
      e.preventDefault();
      const el = document.getElementById(link.scrollTo);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    } else {
      // Different page: navigate home, then scroll after the DOM has mounted
      e.preventDefault();
      setOpen(false);
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(link.scrollTo);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="sticky top-0 z-50 w-full"
      >
      <div className="glass border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-32 sm:h-40">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_vibrant-language-hub/artifacts/8vmii199_Outlined_Logo-Photoroom.webp"
              alt="Language Nest India — Ignite your potential"
              className="h-28 sm:h-36 w-auto object-contain bg-transparent group-hover:scale-[1.04] transition-transform"
              data-testid="nav-logo-img"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={(e) => handleNavClick(e, l)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm font-semibold text-brand-navy hover:text-brand-red transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right cluster: profile/auth controls (top-right of navbar) */}
          <div className="hidden md:flex items-center gap-3" data-testid="nav-right-cluster">
            {user ? (
              <>
                <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-brand-muted-bg" data-testid="nav-user-chip">
                  <User className="w-4 h-4 text-brand-navy" />
                  <span className="text-sm font-semibold text-brand-navy">{user.name || user.email}</span>
                </div>
                {["admin", "sub_admin", "super_admin"].includes(user.role) && (
                  <Link to="/admin" data-testid="nav-admin-link">
                    <Button variant="outline" className="rounded-full border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-semibold">
                      Admin
                    </Button>
                  </Link>
                )}
                <Button
                  variant="outline"
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="nav-logout-btn"
                  className="rounded-full border-brand-red text-brand-red hover:bg-brand-red hover:text-white btn-scale"
                >
                  <LogOut className="w-4 h-4 mr-1" /> Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn">
                  <Button variant="ghost" className="rounded-full font-semibold text-brand-navy hover:bg-brand-muted-bg btn-scale">
                    Login
                  </Button>
                </Link>
                <Link to="/register" data-testid="nav-register-btn">
                  <Button className="rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold brand-shadow btn-scale">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          <button
            data-testid="nav-menu-toggle"
            className="md:hidden text-brand-navy"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden border-t border-white/60 bg-white/80 px-6 py-4 space-y-3"
          >
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={(e) => { handleNavClick(e, l); setOpen(false); }}
                className="block text-brand-navy font-semibold py-1"
              >
                {l.label}
              </Link>
            ))}
            {!user && (
              <div className="flex gap-2 pt-2">
                <Link to="/login" className="flex-1"><Button variant="outline" className="w-full rounded-full">Login</Button></Link>
                <Link to="/register" className="flex-1"><Button className="w-full rounded-full bg-brand-red hover:bg-brand-red-hover">Sign up</Button></Link>
              </div>
            )}
          </motion.div>
        )}
      </div>
      </motion.header>
    </>
  );
};
